import { useCallback, useEffect } from 'react';
import { DIAGNOSTICS_DATE_FORMAT } from '@/const';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { dateRangePickerSelector, RootState } from '@/store/store';
import {
  useLazyGetSummarizedAlarmsQuery,
  useLazyGetTopAlarmsQuery,
  useLazyGetExternalAlarmsQuery,
} from '@/store/api/alarmsApi';

export const useFetchAlarmsData = () => {
  const {
    alarmsDateRange: { from, to },
  } = useSelector((state: RootState) => state.assetsStudio);
  const {
    metric,
    group_by,
    top_ranked,
    chart_group_by,
    asset_types,
    selectedPriorityOptions,
    asset_ids,
    component_ids,
    selectedSeverityOptions,
    selectedInterfaceOptions,
  } = useSelector((state: RootState) => state.alarms);
  const [
    fetchTopAlarms,
    {
      data: topAlarmsData,
      isLoading: isLoadingTopAlarmsData,
      isFetching: isFetchingTopAlarmsData,
      isError: isChartError,
    },
  ] = useLazyGetTopAlarmsQuery();

  const [
    fetchSummarizedAlarmsQuery,
    {
      data: alarmsTableData,
      isLoading: isLoadingAlarmsTableData,
      isFetching: isFetchingAlarmsTableData,
      isError: isTableError,
    },
  ] = useLazyGetSummarizedAlarmsQuery();

  const [fetchExternalAlarms, { data: externalAlarmsData, isLoading, isFetching, isError }] =
    useLazyGetExternalAlarmsQuery();

  const getTopAlarmsData = useCallback(() => {
    const fromDateISO = format(new Date(from), DIAGNOSTICS_DATE_FORMAT);
    const toDateISO = format(new Date(to), DIAGNOSTICS_DATE_FORMAT);
    fetchTopAlarms(
      {
        from_date: fromDateISO,
        to_date: toDateISO,
        metric,
        chart_group_by,
        top_ranked,
        asset_types,
        priority: selectedPriorityOptions,
        asset_ids,
        component_ids,
        severity: selectedSeverityOptions,
        alarm_interface: selectedInterfaceOptions,
      },
      true
    )
      .unwrap()
      .catch(() => {});
  }, [
    component_ids,
    metric,
    chart_group_by,
    top_ranked,
    asset_types,
    selectedPriorityOptions,
    asset_ids,
    selectedSeverityOptions,
    selectedInterfaceOptions,
    from,
    to,
  ]);

  const getAlarmsTableData = useCallback(() => {
    const fromDateISO = format(new Date(from), DIAGNOSTICS_DATE_FORMAT);
    const toDateISO = format(new Date(to), DIAGNOSTICS_DATE_FORMAT);
    fetchSummarizedAlarmsQuery(
      {
        from_date: fromDateISO,
        to_date: toDateISO,
        metric,
        group_by,
        asset_types,
        priority: selectedPriorityOptions,
        asset_ids,
        component_ids,
        severity: selectedSeverityOptions,
        alarm_interface: selectedInterfaceOptions,
      },
      true
    )
      .unwrap()
      .catch(() => {});
  }, [
    component_ids,
    metric,
    group_by,
    top_ranked,
    asset_types,
    selectedPriorityOptions,
    asset_ids,
    selectedSeverityOptions,
    from,
    to,
    selectedInterfaceOptions,
  ]);

  useEffect(() => {
    getAlarmsTableData();
  }, [
    component_ids,
    metric,
    group_by,
    top_ranked,
    asset_types,
    selectedPriorityOptions,
    asset_ids,
    selectedSeverityOptions,
    selectedInterfaceOptions,
    from,
    to,
  ]);

  useEffect(() => {
    getTopAlarmsData();
  }, [
    component_ids,
    metric,
    chart_group_by,
    top_ranked,
    asset_types,
    selectedPriorityOptions,
    asset_ids,
    selectedSeverityOptions,
    selectedInterfaceOptions,
    from,
    to,
  ]);

  return {
    topAlarmsData,
    alarmsTableData,
    isLoadingAlarmsTableData,
    isFetchingAlarmsTableData,
    isLoadingTopAlarmsData,
    isFetchingTopAlarmsData,
    isLoading:
      isLoadingAlarmsTableData || isFetchingAlarmsTableData || isLoadingTopAlarmsData || isFetchingTopAlarmsData,
    isChartError,
    isTableError,
  };
};
